import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Aircraft, AircraftsAPI, AircraftStatus, AircraftCategory } from '../services/aircrafts.ts';
import { formatCurrency } from '../utils/currency.ts';
import { formatStatus, getStatusColor } from '../utils/statusFormatter.ts';
import ConfirmationModal from '../components/ConfirmationModal.tsx';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  PaperAirplaneIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

const Aircrafts: React.FC = () => {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<keyof Aircraft>('purchaseDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | AircraftStatus>(AircraftStatus.IN_STOCK);
  const [categoryFilter, setCategoryFilter] = useState<'all' | AircraftCategory>('all');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [aircraftToDelete, setAircraftToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data: aircrafts,
    isLoading,
    error,
    refetch,
  } = useQuery<Aircraft[]>('aircrafts', AircraftsAPI.list, {
    staleTime: 1000 * 60 * 5,
  });

  const filteredAircrafts =
    aircrafts?.filter(a => {
      const matchesSearch =
        a.make.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.tailNumber.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || a.status === statusFilter;
      const matchesCategory = categoryFilter === 'all' || a.category === categoryFilter;
      return matchesSearch && matchesStatus && matchesCategory;
    }) || [];

  const sortedAircrafts = [...filteredAircrafts].sort((a, b) => {
    const aValue = a[sortField] as any;
    const bValue = b[sortField] as any;
    if (aValue !== undefined && bValue !== undefined) {
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const handleDelete = (id: number) => {
    setAircraftToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!aircraftToDelete) return;
    setIsDeleting(true);
    try {
      await AircraftsAPI.delete(aircraftToDelete);
      refetch();
      setIsDeleteModalOpen(false);
      setAircraftToDelete(null);
    } catch (error) {
      console.error('Erro ao excluir aeronave:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setAircraftToDelete(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500 p-8">
        <p className="text-lg">Erro ao carregar aeronaves.</p>
        <p className="text-sm mt-2">Por favor, tente novamente.</p>
      </div>
    );
  }

  const activeAircrafts = filteredAircrafts?.filter(v => v.status === AircraftStatus.IN_STOCK)
    .length || 0;
  const totalPurchaseValue =
    filteredAircrafts?.reduce((sum, a) => sum + Number(a.purchasePrice || 0), 0) || 0;
  const totalSellingValue =
    filteredAircrafts?.reduce((sum, a) => sum + Number(a.sellingPrice || 0), 0) || 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <PaperAirplaneIcon className="h-8 w-8 mr-3 text-primary-600" />
            Aeronaves
          </h1>
          <p className="text-gray-600">Gerencie sua frota de aeronaves</p>
        </div>
        <button
          onClick={() => navigate('/aircrafts/new')}
          className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Nova Aeronave
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <PaperAirplaneIcon className="h-8 w-8 text-primary-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total de Aeronaves</p>
                <p className="text-2xl font-bold text-gray-900">{filteredAircrafts?.length || 0}</p>
              </div>
            </div>
            <div className="flex items-center">
              <CalendarIcon className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Em Estoque</p>
                <p className="text-2xl font-bold text-gray-900">{activeAircrafts}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CurrencyDollarIcon className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Valor Total de Compra</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalPurchaseValue)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Valor Total de Venda</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalSellingValue)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por marca, modelo ou prefixo..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="min-w-[200px]">
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as 'all' | AircraftStatus)}
              className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="in_stock">Em estoque</option>
              <option value="exchanged">Permutados</option>
              <option value="sold">Vendidos</option>
              <option value="all">Todos</option>
            </select>
          </div>

          {/* Category Filter */}
          <div className="min-w-[220px]">
            <select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value as 'all' | AircraftCategory)}
              className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todas categorias</option>
              <option value="airplane">Avião</option>
              <option value="helicopter">Helicóptero</option>
              <option value="other">Outros</option>
            </select>
          </div>
        </div>
      </div>

      {/* Grid de Aeronaves (agora separado dos filtros) */}
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedAircrafts.map(a => (
            <div
              key={a.id}
              className="bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {a.make} {a.model}
                    </h3>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                        {a.tailNumber}
                      </span>
                      <span className="text-xs text-gray-500">Ano {a.year}</span>
                      {a.category && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          {a.category === 'airplane' ? 'Avião' : a.category === 'helicopter' ? 'Helicóptero' : 'Outros'}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(a.status)}`}>
                    {formatStatus(a.status)}
                  </span>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Valor de Compra:</span>
                    <span className="text-sm font-medium text-gray-900">{formatCurrency(Number(a.purchasePrice || 0))}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Valor de Venda:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {a.sellingPrice ? formatCurrency(Number(a.sellingPrice)) : '-'}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => navigate(`/aircrafts/${a.id}`)}
                    className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-200 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                  >
                    <EyeIcon className="h-4 w-4 mr-1" />
                    Ver
                  </button>
                  {a.assetId && a.status === AircraftStatus.IN_STOCK && (
                    <button
                      onClick={() => navigate(`/exchanges/new?originType=aircraft&originId=${a.assetId}`)}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-purple-200 text-sm font-medium rounded-xl text-purple-700 bg-purple-50 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors duration-200"
                    >
                      Iniciar Permuta
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/edit-aircraft/${a.id}`)}
                    className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-primary-200 text-sm font-medium rounded-xl text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                  >
                    <PencilIcon className="h-4 w-4 mr-1" />
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(a.id)}
                    className="px-3 py-2 border border-red-200 text-sm font-medium rounded-xl text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                    title="Excluir"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {sortedAircrafts.length === 0 && (
          <div className="text-center py-12">
            <PaperAirplaneIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma aeronave encontrada</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'all' || categoryFilter !== 'all' ? 'Tente ajustar os filtros de busca.' : 'Comece cadastrando uma nova aeronave.'}
            </p>
            {!searchTerm && statusFilter === 'all' && categoryFilter === 'all' && (
              <div className="mt-6">
                <button
                  onClick={() => navigate('/aircrafts/new')}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Nova Aeronave
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title="Excluir Aeronave"
        message="Tem certeza que deseja excluir esta aeronave? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Aircrafts;
